import { flip, type FloatingContext, offset, shift, useListNavigation, useTypeahead } from '@floating-ui/react'
import { type MutableRefObject, useRef, useState } from 'react'

/**
 * The floating-ui setup a root menu and a submenu share, so a nested panel is positioned and navigated
 * like the one it opened out of.
 */

export const DEFAULT_SIDE_OFFSET = 4
export const DEFAULT_COLLISION_PADDING = 8

type DropdownMiddlewareOptions = {
    sideOffset?: number
    alignOffset?: number
    avoidCollisions?: boolean
    collisionPadding?: number
}

export const dropdownMiddleware = ({
    sideOffset = DEFAULT_SIDE_OFFSET,
    alignOffset = 0,
    avoidCollisions = true,
    collisionPadding = DEFAULT_COLLISION_PADDING,
}: DropdownMiddlewareOptions) => [
    offset({ mainAxis: sideOffset, alignmentAxis: alignOffset }),
    ...(avoidCollisions ? [flip({ padding: collisionPadding }), shift({ padding: collisionPadding })] : []),
]

/**
 * The items a panel navigates: floating-ui's registry of their elements and labels.
 */
export type DropdownList = {
    elementsRef: MutableRefObject<Array<HTMLElement | null>>
    labelsRef: MutableRefObject<Array<string | null>>
}

type DropdownListOptions = {
    loop: boolean
    /** A submenu's list, which hands ArrowLeft and ArrowRight to the menu it opened from. */
    nested?: boolean
}

/**
 * Arrow-key navigation and typeahead over a panel's items. Returns the list the panel renders its items
 * into, and the interactions to pass to `useInteractions`.
 */
export function useDropdownList(context: FloatingContext, { loop, nested = false }: DropdownListOptions) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null)
    const elementsRef = useRef<Array<HTMLElement | null>>([])
    const labelsRef = useRef<Array<string | null>>([])

    const listNavigation = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        loop,
        nested,
        // 'auto' means the first item is focused when the menu is opened from the keyboard and not when it
        // is clicked open: a highlighted row after a click reads as a choice already made.
        focusItemOnOpen: 'auto',
    })
    const typeahead = useTypeahead(context, {
        listRef: labelsRef,
        activeIndex,
        onMatch: context.open ? setActiveIndex : undefined,
    })

    const list: DropdownList = { elementsRef, labelsRef }
    return { list, interactions: [listNavigation, typeahead] }
}
